import { formatPayRate, hourlyPayrollRate, payTypeDef, resolvePayMethod, type PayFields } from './payTypes';
import { rowsToCsv } from '@/features/reports/csv';

export const DEFAULT_TIMEZONE = 'America/Chicago';
export interface TimeEntry {
  user_id: string;
  clocked_in_at: string;
  clocked_out_at: string | null;
  rate_cents?: number | null;
  pay_type?: string | null;
  job_id?: string | null;
  notes?: string | null;
}
export type CalendarDate = string | Date;

/**
 * N3 elapsed rule, shared word-for-word with `supabase/functions/_shared/elapsed.ts`
 * and SQL `public.format_elapsed_long(integer)` so the feed title, the admin
 * email and the screen all print the same sentence. Under a minute it refuses to
 * name a number; under an hour it is minutes only; at an hour and above all
 * three parts are printed, zeros included. Fixtures:
 * `supabase/functions/_shared/elapsed-fixtures.json`.
 */
export function formatElapsedLong(seconds: number): string {
  const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const part = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;
  if (s < 60) return 'less than a minute';
  if (s < 3600) return part(Math.floor(s / 60), 'minute');
  return `${part(Math.floor(s / 3600), 'hour')} ${part(Math.floor(s % 3600 / 60), 'minute')} ${part(s % 60, 'second')}`;
}

export function organizationTimezone(org: unknown): string {
  const tz = (org as { timezone?: string } | null)?.timezone || DEFAULT_TIMEZONE;
  try { new Intl.DateTimeFormat('en', { timeZone: tz }).format(); return tz; }
  catch { return DEFAULT_TIMEZONE; }
}

function parts(date: Date, tz: string) {
  return Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).map(p => [p.type, p.value]));
}
export function dateInTimezone(date: Date, tz = DEFAULT_TIMEZONE): string {
  const p = parts(date, tz);
  return `${p.year}-${p.month}-${p.day}`;
}
export function addCalendarDays(day: string, count: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + count);
  return d.toISOString().slice(0, 10); // utc-date-ok: UTC-noon arithmetic on a calendar day
}
function dayOf(value: CalendarDate, tz: string) {
  const day = typeof value === 'string' ? value : dateInTimezone(value, tz);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || new Date(`${day}T00:00:00Z`).toISOString().slice(0, 10) !== day) { // utc-date-ok: round-trip validation of a YYYY-MM-DD string
    throw new Error('Invalid calendar date');
  }
  return day;
}
export function startOfOrgDay(day: string, tz = DEFAULT_TIMEZONE): Date {
  const target = Date.parse(`${dayOf(day, tz)}T00:00:00Z`);
  let instant = target;
  for (let i = 0; i < 6; i++) {
    const p = parts(new Date(instant), tz);
    const wall = Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`);
    const correction = target - wall;
    if (!correction) return new Date(instant);
    instant += correction;
  }
  throw new Error('Cannot resolve midnight in organization timezone');
}
/** Monday midnight as an instant in the organization's timezone. */
export function weekStart(date: Date, tz = DEFAULT_TIMEZONE): Date {
  const day = dateInTimezone(date, tz);
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
  return startOfOrgDay(addCalendarDays(day, -((weekday + 6) % 7)), tz);
}
/** Inclusive calendar dates; the returned end instant is exclusive. */
export function timeEntryWindow(from: CalendarDate, to: CalendarDate, tz = DEFAULT_TIMEZONE) {
  const start = startOfOrgDay(dayOf(from, tz), tz);
  const end = startOfOrgDay(addCalendarDays(dayOf(to, tz), 1), tz);
  if (end <= start) throw new Error('Invalid time range');
  return { start, end };
}
export function entryHoursInWindow(entry: Pick<TimeEntry, 'clocked_in_at' | 'clocked_out_at'>, from: CalendarDate, to: CalendarDate, tz = DEFAULT_TIMEZONE) {
  const { start, end } = timeEntryWindow(from, to, tz);
  const open = entry.clocked_out_at === null;
  const hours = open ? 0 : Math.max(0, (Math.min(Date.parse(entry.clocked_out_at!), +end) - Math.max(Date.parse(entry.clocked_in_at), +start)) / 3600000);
  return { hours: Number.isFinite(hours) ? hours : 0, open };
}
export function entryRateCents(entry: Pick<TimeEntry, 'rate_cents' | 'pay_type'>, profile?: PayFields | null): { cents: number | null; source: 'snapshot' | 'current' | 'none' } {
  if (!profile) return { cents: null, source: 'none' };
  // A snapshotted non-hourly unit must never become hourly after a rate change.
  if (entry.pay_type && entry.pay_type !== 'hourly') return { cents: null, source: 'none' };
  if (entry.rate_cents != null && Number.isFinite(entry.rate_cents)) return { cents: entry.rate_cents, source: 'snapshot' };
  const rate = profile ? hourlyPayrollRate(profile) : null;
  return rate === null ? { cents: null, source: 'none' } : { cents: Math.round(rate * 100), source: 'current' };
}

function dailyRateCents(entry: TimeEntry, profile?: PayFields) {
  const resolved = profile ? resolvePayMethod(profile) : null;
  const rate = entry.rate_cents ?? (resolved?.pay_type === 'daily' && resolved.daily_rate != null && resolved.daily_rate !== ''
    ? Math.round(Number(resolved.daily_rate) * 100) : null);
  const cents = profile && rate != null && rate >= 0 && Number.isSafeInteger(rate) ? rate : null;
  const source = cents === null ? 'none' as const : entry.rate_cents != null ? 'snapshot' as const : 'current' as const;
  return { cents, source };
}

/** One daily rate per member/local start day. Conflicting start-day rates are unpriced for review. */
export function dailyPayForEntries(entries: TimeEntry[], profiles: ReadonlyMap<string, PayFields>, from: CalendarDate, to: CalendarDate, tz = DEFAULT_TIMEZONE) {
  const days = new Map<string, { user_id: string; day: string; cents: number | null; source: 'snapshot' | 'current' | 'none' }>();
  const { start, end } = timeEntryWindow(from, to, tz);
  for (const entry of entries) {
    if (!entry.clocked_out_at) continue;
    const profile = profiles.get(entry.user_id);
    const resolved = profile ? resolvePayMethod(profile) : null;
    if ((entry.pay_type ?? resolved?.pay_type) !== 'daily') continue;
    const begin = Date.parse(entry.clocked_in_at);
    const stop = Date.parse(entry.clocked_out_at);
    if (!(begin < stop && begin >= +start && begin < +end)) continue;
    const { cents, source } = dailyRateCents(entry, profile);
    const day = dateInTimezone(new Date(begin), tz);
    const key = `${entry.user_id}:${day}`;
    const previous = days.get(key);
    const conflict = previous && previous.cents !== cents;
    days.set(key, { user_id: entry.user_id, day, cents: conflict ? null : cents,
      source: conflict || (previous && previous.source !== source) ? 'none' : source });
  }
  return days;
}

export interface PayrollPerson { name: string; email: string; role?: string | null }
export function formatCsvPayroll(entries: TimeEntry[], profiles: ReadonlyMap<string, PayFields>, people: ReadonlyMap<string, PayrollPerson>, from: CalendarDate, to: CalendarDate, tz = DEFAULT_TIMEZONE) {
  const { start, end } = timeEntryWindow(from, to, tz);
  const daily = dailyPayForEntries(entries, profiles, from, to, tz);
  const paidDays = new Set<string>();
  const detailRows: Record<string, string>[] = [];
  const openRows: Record<string, string>[] = [];
  const totals = new Map<string, { hours: number; cents: number; missing: boolean; rates: Set<string>; sources: Set<string>; types: Set<string>; payRates: Set<string> }>();
  for (const entry of entries) {
    if (Date.parse(entry.clocked_in_at) >= +end || (entry.clocked_out_at && Date.parse(entry.clocked_out_at) <= +start)) continue;
    const person = people.get(entry.user_id);
    const identity = { employee: person?.name ?? entry.user_id, email: person?.email ?? '', role: person?.role ?? '' };
    if (!entry.clocked_out_at) {
      openRows.push({ ...identity, clock_in: entry.clocked_in_at, timezone: tz, job_id: entry.job_id ?? '', notes: entry.notes ?? '' });
      continue;
    }
    const rate = entryRateCents(entry, profiles.get(entry.user_id));
    const rawProfile = profiles.get(entry.user_id);
    const profile = rawProfile ? resolvePayMethod(rawProfile) : undefined;
    const payType = entry.pay_type ?? profile?.pay_type ?? '';
    const payLabel = profile?.pay_method_id && profile.basis && payType === profile.pay_type ? profile.label : payType;
    const rateColumn = payTypeDef(payType).rateColumn;
    const payFields = profile && entry.rate_cents != null && rateColumn
      ? profile.pay_method_id && profile.basis && payType === profile.pay_type
        ? { ...profile, [rateColumn]: entry.rate_cents / 100, pay_method_rate: entry.rate_cents / 100 }
        : { pay_type: payType, [rateColumn]: entry.rate_cents / 100, custom_pay_label: profile.custom_pay_label }
      : profile;
    const payRate = payFields ? formatPayRate(payFields) ?? '' : '';
    const total = totals.get(entry.user_id) ?? { hours: 0, cents: 0, missing: false, rates: new Set<string>(), sources: new Set<string>(), types: new Set<string>(), payRates: new Set<string>() };
    const startDay = dateInTimezone(new Date(entry.clocked_in_at), tz);
    const dailyKey = `${entry.user_id}:${startDay}`;
    const dayPay = payType === 'daily' ? daily.get(dailyKey) : undefined;
    let day = dateInTimezone(new Date(Math.max(Date.parse(entry.clocked_in_at), +start)), tz);
    const stop = Math.min(Date.parse(entry.clocked_out_at), +end);
    while (+startOfOrgDay(day, tz) < stop) {
      const { hours } = entryHoursInWindow(entry, day, day, tz);
      if (hours > 0) {
        const payCents = payType === 'daily' ? day !== startDay || paidDays.has(dailyKey) ? 0 : dayPay?.cents ?? null
          : rate.cents === null ? null : Math.round(hours * rate.cents);
        const rateSource = payType === 'daily' ? dayPay?.source ?? dailyRateCents(entry, rawProfile).source : rate.source;
        if (dayPay && day === startDay) paidDays.add(dailyKey);
        detailRows.push({ ...identity, date: day, timezone: tz, clock_in: entry.clocked_in_at, clock_out: entry.clocked_out_at,
          hours: hours.toFixed(2), pay_type: payLabel, pay_rate: payRate, hourly_rate: rate.cents === null ? '' : (rate.cents / 100).toFixed(2),
          rate_source: rateSource, pay: payCents === null ? '' : (payCents / 100).toFixed(2), job_id: entry.job_id ?? '', notes: entry.notes ?? '' });
        total.hours += hours;
        total.cents += payCents ?? 0;
        total.missing ||= payCents === null;
        total.rates.add(rate.cents === null ? '' : (rate.cents / 100).toFixed(2));
        total.sources.add(rateSource);
        total.types.add(payLabel);
        total.payRates.add(payRate);
      }
      day = addCalendarDays(day, 1);
    }
    totals.set(entry.user_id, total);
  }
  const summaryRows = Array.from(totals, ([userId, total]) => ({ employee: people.get(userId)?.name ?? userId,
    email: people.get(userId)?.email ?? '', total_hours: total.hours.toFixed(2), pay_type: [...total.types].join('|'), pay_rate: [...total.payRates].join(' | '),
    hourly_rate: total.rates.size === 1 ? [...total.rates][0] : '', rate_source: total.sources.size === 1 ? [...total.sources][0] : '',
    gross_pay: total.missing ? '' : (total.cents / 100).toFixed(2), user_id: userId }));
  const openSection = `\r\n\r\nOpen punches\r\n${rowsToCsv(openRows)}`;
  return { detailRows, summaryRows, openRows, detailCsv: rowsToCsv(detailRows) + openSection, summaryCsv: rowsToCsv(summaryRows) + openSection };
}

/** Per-job cents at the scheduling snapshot, else current per-foot rate; actual zero wins. */
export function lfPayForJob(
  job: { pay_lf: number | string | null; actual_lf: number | string | null; assigned_to: string | null; pay_lf_rate_cents?: number | null },
  profile: PayFields | null | undefined,
): { feet: number | null; cents: number | null; source: 'actual' | 'scheduled' | 'none'; rate_source: 'snapshot' | 'current' } {
  profile = profile ? resolvePayMethod(profile) : profile;
  const snapshot = job.pay_lf_rate_cents != null && Number.isFinite(job.pay_lf_rate_cents);
  const rate_source: 'snapshot' | 'current' = snapshot ? 'snapshot' : 'current';
  const none = { feet: null, cents: null, source: 'none' as const, rate_source };
  if (!job.assigned_to || (!snapshot && profile?.pay_type !== 'per_linear_foot')) return none;
  const raw = job.actual_lf ?? job.pay_lf;
  if (raw == null || (typeof raw === 'string' && !raw.trim())) return none;
  const feet = Number(raw);
  if (!Number.isFinite(feet) || feet < 0) return none;
  const source = job.actual_lf != null ? 'actual' : 'scheduled';
  const rawRate = profile?.rate_per_linear_foot;
  const rate = snapshot ? job.pay_lf_rate_cents! / 100
    : rawRate == null || (typeof rawRate === 'string' && !rawRate.trim()) ? NaN : Number(rawRate);
  const cents = Math.round((feet * rate + Number.EPSILON * Math.abs(feet * rate)) * 100);
  return { feet, cents: rate >= 0 && Number.isSafeInteger(cents) ? cents : null, source, rate_source };
}

/**
 * The org-zone wall clock, as `YYYY-MM-DDTHH:MM:SS`.
 *
 * Everything on this tab is anchored to the ORGANISATION's timezone, not the
 * browser's: payroll is counted that way (CONTRACT §3 E1), so a dispatcher in
 * Denver looking at a Texas crew has to read the same day boundaries the CSV
 * uses. Lane E's `timeEntries.ts` owns the calendar maths; these two helpers are
 * the wall-clock ↔ instant pair its exports do not cover, needed because the
 * entry form takes a wall time and the database takes an instant.
 */
function orgWallParts(date: Date, tz: string): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}`;
}

/** `YYYY-MM-DDTHH:MM` as a clock in `tz` would read it — what `datetime-local` wants. */
export function orgWallTime(date: Date, tz: string): string {
  return orgWallParts(date, tz).slice(0, 16);
}

/**
 * The instant at which the clock in `tz` reads `local` (`YYYY-MM-DDTHH:MM`).
 *
 * Fixed-point correction rather than a fixed offset, so it stays right across a
 * DST change — the same technique Lane E's `startOfOrgDay` uses for midnight.
 * Returns null for a malformed string; a wall time that does not exist (the
 * spring-forward gap) settles on the nearest real instant instead of throwing.
 */
export function orgInstant(local: string, tz: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;
  const target = Date.parse(`${local}:00Z`);
  if (!Number.isFinite(target)) return null;
  let instant = target;
  for (let i = 0; i < 6; i += 1) {
    const wall = Date.parse(`${orgWallParts(new Date(instant), tz)}Z`);
    const correction = target - wall;
    if (!correction) return new Date(instant);
    instant += correction;
  }
  return new Date(instant);
}
